import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterColumn, ActiveFilter } from '../../../types/filters';
import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';
import { useTabPaneContext } from '../routing/TabPaneContext';
import { loadPanePersistedFilters, savePanePersistedFilters } from './paneFilterStorage';

export interface UseDynamicFiltersOptions {
  /** Escopo da chave no sessionStorage (default: "default"). */
  scope?: string;
  /** Persistir filtros entre abas/fechamentos quando keep-alive está ativo (default: true). */
  persist?: boolean;
}

const readPersistedFilters = (
  pathname: string | null,
  scope: string,
  fallback: ActiveFilter[],
): ActiveFilter[] => {
  if (!pathname) return fallback;
  const stored = loadPanePersistedFilters(pathname, scope);
  return stored ?? fallback;
};

export const useDynamicFilters = (
  columns: FilterColumn[],
  initialActiveFilters: ActiveFilter[] = [],
  options?: UseDynamicFiltersOptions,
) => {
  const paneContext = useTabPaneContext();
  const location = useLocation();
  const scope = options?.scope ?? 'default';
  const persistEnabled =
    (options?.persist ?? true) && isTabKeepAliveEnabled();
  const panePath = useMemo(
    () => (persistEnabled ? paneContext?.panePath ?? location.pathname : null),
    [persistEnabled, paneContext?.panePath, location.pathname],
  );

  const panePathRef = useRef(panePath);
  panePathRef.current = panePath;

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilters, setActiveFiltersState] = useState<ActiveFilter[]>(() =>
    persistEnabled
      ? readPersistedFilters(
          paneContext?.panePath ?? location.pathname,
          scope,
          initialActiveFilters,
        )
      : initialActiveFilters,
  );
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);

  const persistFilters = useCallback(
    (filters: ActiveFilter[]) => {
      if (!persistEnabled || !panePathRef.current) return;
      savePanePersistedFilters(panePathRef.current, scope, filters);
    },
    [persistEnabled, scope],
  );

  const setActiveFilters = useCallback<
    React.Dispatch<React.SetStateAction<ActiveFilter[]>>
  >(
    (value) => {
      setActiveFiltersState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        persistFilters(next);
        return next;
      });
    },
    [persistFilters],
  );

  useEffect(() => {
    if (!persistEnabled || !panePath) return;

    const stored = loadPanePersistedFilters(panePath, scope);
    if (stored !== null) {
      setActiveFiltersState(stored);
      return;
    }

    if (activeFilters.length === 0 && initialActiveFilters.length > 0) {
      setActiveFiltersState(initialActiveFilters);
      persistFilters(initialActiveFilters);
    }
    // Re-hidrata ao trocar de pane (ex.: remount keep-alive).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panePath, persistEnabled, scope]);

  const handleAddFilter = useCallback(() => {
    if (selectedColumn && filterValue) {
      const column = columns.find(col => col.value === selectedColumn);
      let valueForLabel = filterValue;
      if (column?.type === 'select' && column.options?.length) {
        const opt = column.options.find(o => String(o.value) === String(filterValue));
        if (opt) valueForLabel = opt.label;
      }
      const newFilter: ActiveFilter = {
        id: Date.now().toString(),
        column: selectedColumn,
        value: filterValue,
        label: `${column?.label}: ${valueForLabel}`,
        columnLabel: column?.label || selectedColumn
      };

      if (editingFilterId) {
        setActiveFilters(prev => prev.map(f => f.id === editingFilterId ? newFilter : f));
        setEditingFilterId(null);
      } else {
        setActiveFilters(prev => [...prev, newFilter]);
      }

      setSelectedColumn('');
      setFilterValue('');
      setFilterDialogOpen(false);
    }
  }, [selectedColumn, filterValue, columns, editingFilterId, setActiveFilters]);

  const handleEditFilter = useCallback((filter: ActiveFilter) => {
    setEditingFilterId(filter.id);
    setSelectedColumn(filter.column);
    setFilterValue(filter.value);
    setFilterDialogOpen(true);
  }, []);

  const handleDeleteFilter = useCallback((filterId: string) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  }, [setActiveFilters]);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
  }, [setActiveFilters]);

  const openFilterDialog = useCallback(() => {
    setFilterDialogOpen(true);
  }, []);

  const closeFilterDialog = useCallback(() => {
    setFilterDialogOpen(false);
    setSelectedColumn('');
    setFilterValue('');
    setEditingFilterId(null);
  }, []);

  const openFilterForColumn = useCallback(
    (columnValue: string): boolean => {
      const exists = columns.some((col) => col.value === columnValue);
      if (!exists) return false;
      setEditingFilterId(null);
      setFilterValue('');
      setSelectedColumn(columnValue);
      setFilterDialogOpen(true);
      return true;
    },
    [columns]
  );

  const getColumnOptions = useCallback(() => columns, [columns]);

  const getColumnLabel = useCallback((columnValue: string) => {
    const column = columns.find(col => col.value === columnValue);
    return column?.label || columnValue;
  }, [columns]);

  return {
    filterDialogOpen,
    selectedColumn,
    filterValue,
    activeFilters,
    editingFilterId,
    setSelectedColumn,
    setFilterValue,
    setActiveFilters,
    handleAddFilter,
    handleEditFilter,
    handleDeleteFilter,
    clearAllFilters,
    openFilterDialog,
    closeFilterDialog,
    openFilterForColumn,
    getColumnOptions,
    getColumnLabel
  };
};
